'use client';

import clsx from 'clsx';

import { type BreadcrumbItem } from '@/components/ui/Breadcrumbs';
import { formatLabel } from '@/components/ui/Breadcrumbs/components/Breadcrumbs.shared';
import { useAriaLabels } from '@/hooks/useAriaLabels';
import { Link } from '@/lib/i18n/navigation';

import styles from './PdpBreadcrumbsBar.module.scss';

export interface PdpBreadcrumbsBarProps {
  items: BreadcrumbItem[];
  /**
   * Light text on a transparent bar, for dark contexts. Driven by the CMS
   * Breadcrumb entry's `style` field ('Light'). BEC-24794.
   */
  inverted?: boolean;
}

/**
 * Full-width breadcrumb strip rendered above the PDP product detail block.
 * Gallet design: slash separators, muted gray text, trailing slash on last item.
 */
export function PdpBreadcrumbsBar({ items, inverted = false }: Readonly<PdpBreadcrumbsBarProps>) {
  const aria = useAriaLabels();

  if (!items.length) {
    return null;
  }

  return (
    <div className={clsx(styles.bar, inverted && styles.inverted)}>
      <nav className={styles.inner} aria-label={aria.action('breadcrumb')}>
        <ol className={styles.list}>
          {items.map((item, index) => (
            <li key={`${item.label}-${index}`} className={styles.item}>
              {item.href ? (
                <Link href={item.href} className={styles.link}>
                  {formatLabel(item.label)}
                </Link>
              ) : (
                <span className={styles.current} aria-current="page">
                  {formatLabel(item.label)}
                </span>
              )}
              <span className={styles.separator} aria-hidden="true">
                /
              </span>
            </li>
          ))}
        </ol>
      </nav>
    </div>
  );
}

PdpBreadcrumbsBar.displayName = 'PdpBreadcrumbsBar';
